/**
 * Progress engine for the client-onboarding checklist. Pure functions over the
 * catalogue and a tracker's persisted step rows; used by the API (to persist
 * `overallStatus`, answer Control Center) and by the web (to draw the rings
 * before the save round-trips).
 */
import { ONBOARDING_PHASES, STEP_INDEX, ALL_STEP_IDS, phaseOf } from "./catalogue.js";
import {
  DEFAULT_ONBOARDING_MODULES,
  ONBOARDING_MODULES,
  ONBOARDING_STEP_STATUSES,
  type OnboardingModuleFlags,
  type OnboardingPhase,
  type OnboardingPhaseSummary,
  type OnboardingStep,
  type OnboardingStepState,
  type OnboardingStepStatus,
  type OnboardingSummary,
  type OnboardingTrackerStatus,
} from "./types.js";

const STATUS_SET = new Set<string>(ONBOARDING_STEP_STATUSES);

export function isOnboardingStepStatus(v: unknown): v is OnboardingStepStatus {
  return typeof v === "string" && STATUS_SET.has(v);
}

/** Coerce whatever is in the tracker's `modules` JSON into a full flag set. */
export function normalizeModules(raw: unknown): OnboardingModuleFlags {
  const out: OnboardingModuleFlags = { ...DEFAULT_ONBOARDING_MODULES };
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const key of ONBOARDING_MODULES) {
      if (typeof o[key] === "boolean") out[key] = o[key] as boolean;
    }
  }
  return out;
}

/** The module gate that applies to a step: its own, else its phase's. */
export function stepModule(step: OnboardingStep): OnboardingPhase["module"] | undefined {
  return step.module ?? phaseOf(step.id)?.module;
}

/** Does this step count for a school with these module flags? */
export function isStepApplicable(step: OnboardingStep, modules: OnboardingModuleFlags): boolean {
  const mod = stepModule(step);
  return mod ? modules[mod] : true;
}

/** A step may be marked "Not applicable" when it is optional or its module is off. */
export function canSkipStep(step: OnboardingStep, modules: OnboardingModuleFlags): boolean {
  return !step.required || !isStepApplicable(step, modules);
}

/**
 * Bring persisted rows up to the current catalogue: every catalogue step gets
 * exactly one row (in catalogue order), unknown ids are dropped, bad statuses
 * fall back to `pending`, and steps whose module is OFF read as `skipped`
 * (steps whose module is ON but were auto-skipped earlier come back as
 * `pending`). Idempotent; the API calls it on every read and write.
 */
export function normalizeSteps(raw: unknown, modules: OnboardingModuleFlags): OnboardingStepState[] {
  const byId = new Map<string, Partial<OnboardingStepState>>();
  if (Array.isArray(raw)) {
    for (const row of raw) {
      if (row && typeof row === "object" && typeof (row as { id?: unknown }).id === "string") {
        byId.set((row as { id: string }).id, row as Partial<OnboardingStepState>);
      }
    }
  }
  return ALL_STEP_IDS.map((id) => {
    const step = STEP_INDEX.get(id)!;
    const prev = byId.get(id);
    const applicable = isStepApplicable(step, modules);
    let status: OnboardingStepStatus = isOnboardingStepStatus(prev?.status) ? prev.status : "pending";
    const comment = typeof prev?.comment === "string" && prev.comment.trim() ? prev.comment : null;
    if (!applicable) {
      status = "skipped";
    } else if (status === "skipped" && step.required) {
      // The module came back on (or the row was written by an older client
      // with no skip rule): a required step cannot stay "not applicable".
      status = "pending";
    }
    return {
      id,
      status,
      comment: applicable ? comment : comment ?? null,
      completedAt: typeof prev?.completedAt === "string" ? prev.completedAt : null,
      updatedAt: typeof prev?.updatedAt === "string" ? prev.updatedAt : null,
    };
  });
}

function emptySummary(): Omit<OnboardingSummary, "status" | "progress"> {
  return { total: 0, applicable: 0, completed: 0, inProgress: 0, blocked: 0, skipped: 0, pending: 0, minutesLeft: 0 };
}

function finish(acc: Omit<OnboardingSummary, "status" | "progress">): OnboardingSummary {
  const progress = acc.applicable === 0 ? 0 : Math.round((acc.completed / acc.applicable) * 100);
  let status: OnboardingTrackerStatus;
  if (acc.applicable > 0 && acc.completed === acc.applicable) status = "COMPLETED";
  else if (acc.completed === 0 && acc.inProgress === 0 && acc.blocked === 0) status = "NOT_STARTED";
  else status = "IN_PROGRESS";
  return { ...acc, progress, status };
}

function tally(acc: Omit<OnboardingSummary, "status" | "progress">, step: OnboardingStep, state: OnboardingStepState | undefined) {
  acc.total += 1;
  const status = state?.status ?? "pending";
  if (status === "skipped") {
    acc.skipped += 1;
    return;
  }
  acc.applicable += 1;
  if (status === "completed") acc.completed += 1;
  else {
    acc.minutesLeft += step.minutes;
    if (status === "in_progress") acc.inProgress += 1;
    else if (status === "blocked") acc.blocked += 1;
    else acc.pending += 1;
  }
}

/** Whole-tracker summary. `states` should already be normalized. */
export function summarizeSteps(states: readonly OnboardingStepState[]): OnboardingSummary {
  const byId = new Map(states.map((s) => [s.id, s]));
  const acc = emptySummary();
  for (const phase of ONBOARDING_PHASES) {
    for (const section of phase.sections) {
      for (const step of section.steps) tally(acc, step, byId.get(step.id));
    }
  }
  return finish(acc);
}

export function summarizePhase(phase: OnboardingPhase, states: readonly OnboardingStepState[]): OnboardingPhaseSummary {
  const byId = new Map(states.map((s) => [s.id, s]));
  const acc = emptySummary();
  for (const section of phase.sections) {
    for (const step of section.steps) tally(acc, step, byId.get(step.id));
  }
  return { phaseId: phase.id, ...finish(acc) };
}

export function summarizePhases(states: readonly OnboardingStepState[]): OnboardingPhaseSummary[] {
  return ONBOARDING_PHASES.map((p) => summarizePhase(p, states));
}

/**
 * The step an agent should look at next: the first applicable step, in
 * catalogue order, that is not completed. Blocked and in-progress steps count
 * as "next" too — they are where the work is.
 */
export function nextStep(states: readonly OnboardingStepState[]): OnboardingStep | null {
  const byId = new Map(states.map((s) => [s.id, s]));
  for (const id of ALL_STEP_IDS) {
    const status = byId.get(id)?.status ?? "pending";
    if (status === "completed" || status === "skipped") continue;
    return STEP_INDEX.get(id) ?? null;
  }
  return null;
}

/** Prerequisites of `step` that are not yet completed (and not skipped). */
export function unmetDependencies(step: OnboardingStep, states: readonly OnboardingStepState[]): OnboardingStep[] {
  if (!step.dependsOn?.length) return [];
  const byId = new Map(states.map((s) => [s.id, s]));
  const out: OnboardingStep[] = [];
  for (const depId of step.dependsOn) {
    const dep = STEP_INDEX.get(depId);
    if (!dep) continue;
    const status = byId.get(depId)?.status ?? "pending";
    if (status !== "completed" && status !== "skipped") out.push(dep);
  }
  return out;
}

/** Applicable steps that are `blocked`, in catalogue order. */
export function blockedSteps(states: readonly OnboardingStepState[]): Array<{ step: OnboardingStep; state: OnboardingStepState }> {
  const out: Array<{ step: OnboardingStep; state: OnboardingStepState }> = [];
  for (const state of states) {
    if (state.status !== "blocked") continue;
    const step = STEP_INDEX.get(state.id);
    if (step) out.push({ step, state });
  }
  return out;
}
